import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  ArrowLeft, 
  CheckCircle2, 
  AlertCircle,
  Wallet,
  Building2,
  CreditCard,
  FileText,
  Lock,
  XCircle
} from 'lucide-react';
import MenuClient from '../Components/menuclient';

const API = 'https://appapi.unpaidfinancial.com/api';

const fmtMoney = (n, dev = 'EUR') =>
  `${Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${dev}`;

const Virement = () => {
  const history = useHistory();

  const session = useMemo(() => {
    try {
      const raw = localStorage.getItem('bank.session');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const token = useMemo(() => {
    try {
      return localStorage.getItem('bank.token') || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!session || !session.user || !session.account || !token) {
      history.replace('/banque/login');
    }
  }, [session, token, history]);

  const balance = session?.account?.solde ?? 0;
  const devise = session?.account?.devise ?? 'EUR';
  const fromUserId = session?.user?.id;

  const [form, setForm] = useState({
    montant: '',
    raison: '',
    iban: '',
    swift: '',
    bankName: '',
  });

  const [loading, setLoading] = useState(false);
  const [notif, setNotif] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [blockMessage, setBlockMessage] = useState('');
  const [transferSuccess, setTransferSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'iban') {
      const formatted = value.replace(/\s/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();
      setForm((f) => ({ ...f, iban: formatted }));
      return;
    } else if (name === 'swift') {
      setForm((f) => ({ ...f, swift: value.toUpperCase() }));
      return;
    } else if (name === 'activationCode') {
      const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 20);
      setActivationCode(cleaned);
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validateForm = () => {
    const amountNum = Number(form.montant);
    
    if (!form.montant || isNaN(amountNum) || amountNum <= 0) {
      setNotif({ type: 'error', message: 'Veuillez entrer un montant valide.' });
      return false;
    }
    
    if (amountNum > Number(balance)) {
      setNotif({ type: 'error', message: 'Solde insuffisant pour ce virement.' });
      return false;
    }
    
    if (!form.iban) {
      setNotif({ type: 'error', message: 'Veuillez entrer un IBAN.' });
      return false;
    }
    
    if (!form.swift || form.swift.length < 8 || form.swift.length > 11) {
      setNotif({ type: 'error', message: 'Le code SWIFT doit contenir entre 8 et 11 caractères.' });
      return false;
    }
    
    if (!form.bankName || form.bankName.trim().length < 3) {
      setNotif({ type: 'error', message: 'Veuillez entrer le nom de la banque.' });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotif(null);

    // Validation du formulaire
    if (!showSummary && !validateForm()) {
      return;
    }

    // Passer à l'écran de résumé
    if (!showSummary) {
      setShowSummary(true);
      return;
    }

    // Traitement de la confirmation
    if (showSummary && !showResult) {
      if (!activationCode || activationCode.length < 4 || activationCode.length > 20) {
        setNotif({ type: 'error', message: 'Le code d\'activation doit contenir entre 4 et 20 caractères (lettres et chiffres).' });
        return;
      }
      
      setLoading(true);
      
      try {
        // Étape 1 : Vérifier le block_percent AVANT d'envoyer
        const checkRes = await fetch(`${API}/bank/check-block-status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            user_id: fromUserId,
          }),
        });

        const checkPayload = await checkRes.json().catch(() => null);

        if (!checkRes.ok) {
          const errMsg = (checkPayload && (checkPayload.error || checkPayload.message)) || 'Erreur lors de la vérification du compte.';
          setNotif({ type: 'error', message: errMsg });
          setLoading(false);
          return;
        }

        const blockPercent = checkPayload?.block_percent ?? 0;
        const blockMsg = checkPayload?.block_message || '';

        console.log('Block percent:', blockPercent);
        console.log('Block message:', blockMsg);

        // Si block_percent < 100%, afficher l'erreur SANS envoyer la requête /send
        if (blockPercent < 100) {
          console.log('Virement bloqué - Affichage du message d\'erreur');
          setTransferSuccess(false);
          setBlockMessage(blockMsg || 'Votre compte ne permet pas d\'effectuer ce virement. Veuillez contacter votre gestionnaire.');
          setShowResult(true);
          setLoading(false);
          return; // ❌ STOP ICI - On ne continue PAS
        }

        // Étape 2 : Si block_percent = 100%, envoyer la requête /send
        console.log('Block percent = 100% - Envoi du virement');
        const res = await fetch(`${API}/bank/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            from_user_id: fromUserId,
            amount: Number(form.montant),
            activation_code: activationCode,
            raison: form.raison || undefined,
            recipient_iban: form.iban,
            recipient_swift: form.swift,
            recipient_bank_name: form.bankName,
          }),
        });

        const payload = await res.json().catch(() => null);

        if (!res.ok) {
          const errMsg =
            (payload && (payload.error || payload.message)) ||
            `Échec du virement (HTTP ${res.status}).`;
          setNotif({ type: 'error', message: errMsg });
          setLoading(false);
          return;
        }

        // Succès : le virement est effectué
        console.log('Virement réussi !');
        setTransferSuccess(true);
        setShowResult(true);
        
        // Mettre à jour le solde en session
        if (session?.account) {
          const newBalance = Number(balance) - Number(form.montant);
          session.account.solde = newBalance;
          localStorage.setItem('bank.session', JSON.stringify(session));
        }
        
      } catch (err) {
        console.error('Erreur:', err);
        const msg =
          err?.message?.includes('Failed to fetch')
            ? "Impossible de joindre l'API. Vérifiez que le backend est démarré."
            : err?.message || 'Erreur réseau inconnue.';
        setNotif({ type: 'error', message: msg });
      } finally {
        setLoading(false);
      }
      
      return;
    }
  };

  const handleRetry = () => {
    setShowResult(false);
    setShowSummary(false);
    setNotif(null);
    setActivationCode('');
    setBlockMessage('');
    setTransferSuccess(false);
  };

  const handleBackToDashboard = () => {
    history.push('/banque/portefeuille');
  };

  if (!session || !session.user || !session.account || !token) {
    return null;
  }

  return (
    <div style={styles.page}>
      {/* Hero Section */}
      <motion.div 
        style={styles.heroSection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div style={styles.heroContent}>
          <motion.div 
            style={styles.headerTop}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div style={styles.logoContainer}>
              <Send size={28} strokeWidth={2.5} />
              <span style={styles.pageTitle}>Virement</span>
            </div>
          </motion.div>

          <motion.div
            style={styles.balanceCard}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 100 }}
          >
            <div style={styles.balanceLabel}>Solde disponible</div>
            <div style={styles.balanceAmount}>{fmtMoney(balance, devise)}</div>
          </motion.div>

          <motion.div
            style={styles.warningBox}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <AlertCircle size={16} />
            <span style={styles.warningText}>
              Virement sécurisé et instantané
            </span>
          </motion.div>
        </div>
      </motion.div>

      {/* Notification */}
      <AnimatePresence>
        {notif && (
          <motion.div
            style={{
              ...styles.notifBox,
              background: notif.type === 'success' ? '#DCFCE7' : '#FEE2E2',
              borderLeft: `4px solid ${notif.type === 'success' ? '#16A34A' : '#DC2626'}`,
            }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {notif.type === 'success' ? (
              <CheckCircle2 size={20} color="#16A34A" />
            ) : (
              <AlertCircle size={20} color="#DC2626" />
            )}
            <span style={{ color: notif.type === 'success' ? '#065F46' : '#991B1B' }}>
              {notif.message}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contenu principal */}
      <div style={styles.contentContainer}>
        <AnimatePresence mode="wait">
          {!showSummary ? (
            // Formulaire initial
            <motion.form
              key="form"
              style={styles.card}
              onSubmit={handleSubmit}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <Wallet size={16} />
                  Montant du virement
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    name="montant"
                    placeholder="0.00"
                    value={form.montant}
                    onChange={handleChange}
                    style={styles.input}
                    min="0"
                    step="0.01"
                    disabled={loading}
                  />
                  <span style={styles.inputSuffix}>{devise}</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <CreditCard size={16} />
                  IBAN du bénéficiaire
                </label>
                <input
                  type="text"
                  name="iban"
                  value={form.iban}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                  disabled={loading}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <Building2 size={16} />
                  Code SWIFT/BIC
                </label>
                <input
                  type="text"
                  name="swift"
                  value={form.swift}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="Ex: AGBKFRPP"
                  disabled={loading}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <Building2 size={16} />
                  Nom de la banque
                </label>
                <input
                  type="text"
                  name="bankName"
                  value={form.bankName}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="Ex: BNP Paribas"
                  disabled={loading}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <FileText size={16} />
                  Raison du virement (optionnel)
                </label>
                <input
                  type="text"
                  name="raison"
                  value={form.raison}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="Ex: Loyer, remboursement..."
                  disabled={loading}
                />
              </div>

              <motion.button
                type="submit"
                style={styles.primaryButton}
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Send size={18} />
                Continuer
              </motion.button>
            </motion.form>
          ) : !showResult ? (
            // Écran de confirmation
            <motion.div
              key="summary"
              style={styles.card}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <h3 style={styles.cardTitle}>Résumé du virement</h3>

              <div style={styles.summarySection}>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Montant</span>
                  <span style={styles.summaryValue}>{fmtMoney(form.montant, devise)}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>IBAN</span>
                  <span style={styles.summaryValueSmall}>{form.iban}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Banque</span>
                  <span style={styles.summaryValueSmall}>{form.bankName}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>SWIFT</span>
                  <span style={styles.summaryValueSmall}>{form.swift}</span>
                </div>
                {form.raison && (
                  <div style={styles.summaryRow}>
                    <span style={styles.summaryLabel}>Raison</span>
                    <span style={styles.summaryValueSmall}>{form.raison}</span>
                  </div>
                )}
              </div>

              <div style={styles.activationSection}>
                <label style={styles.label}>
                  <Lock size={16} />
                  Code d'activation
                </label>
                <input
                  type="text"
                  name="activationCode"
                  value={activationCode}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="Entrez votre code (4-20 caractères)"
                  maxLength={20}
                  disabled={loading}
                />
                <small style={styles.hint}>Lettres et chiffres uniquement</small>
              </div>

              <div style={styles.buttonGroup}>
                <motion.button
                  type="button"
                  onClick={() => {
                    setShowSummary(false);
                    setActivationCode('');
                  }}
                  style={styles.secondaryButton}
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ArrowLeft size={18} />
                  Retour
                </motion.button>
                <motion.button
                  type="button"
                  onClick={handleSubmit}
                  style={{
                    ...styles.primaryButton,
                    opacity: (!activationCode || activationCode.length < 4 || loading) ? 0.5 : 1,
                    cursor: (!activationCode || activationCode.length < 4 || loading) ? 'not-allowed' : 'pointer',
                  }}
                  disabled={!activationCode || activationCode.length < 4 || loading}
                  whileHover={{ scale: (!activationCode || activationCode.length < 4 || loading) ? 1 : 1.02 }}
                  whileTap={{ scale: (!activationCode || activationCode.length < 4 || loading) ? 1 : 0.98 }}
                >
                  {loading ? 'Vérification...' : (
                    <>
                      <CheckCircle2 size={18} />
                      Confirmer
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          ) : (
            // Écran de résultat
            <motion.div
              key="result"
              style={styles.card}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {transferSuccess ? (
                // Succès - Virement effectué (block_percent = 100%)
                <motion.div
                  style={styles.successCard}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <CheckCircle2 size={64} color="#16A34A" strokeWidth={2} />
                  <h4 style={styles.successTitle}>Virement effectué</h4>
                  <p style={styles.successText}>
                    Le montant de <strong>{fmtMoney(form.montant, devise)}</strong> a été transféré avec succès vers le compte bénéficiaire.
                  </p>
                  <div style={styles.successDetails}>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>IBAN :</span>
                      <span style={styles.detailValue}>{form.iban}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Banque :</span>
                      <span style={styles.detailValue}>{form.bankName}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Nouveau solde :</span>
                      <span style={styles.detailValue}>{fmtMoney(Number(balance) - Number(form.montant), devise)}</span>
                    </div>
                  </div>
                  <motion.button
                    onClick={handleBackToDashboard}
                    style={styles.successButton}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <CheckCircle2 size={18} />
                    Retour au tableau de bord
                  </motion.button>
                </motion.div>
              ) : (
                // Erreur - Virement bloqué (block_percent < 100%)
                <motion.div
                  style={styles.errorCard}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <XCircle size={64} color="#DC2626" strokeWidth={2} />
                  <h4 style={styles.errorTitle}>Erreur de virement</h4>
                  <div style={styles.errorMessageCard}>
                    <p style={styles.errorMessageText}>
                      {blockMessage}
                    </p>
                  </div>
                  <motion.button
                    onClick={handleRetry}
                    style={styles.retryButton}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ArrowLeft size={18} />
                    Retour au formulaire
                  </motion.button>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <MenuClient />
      <div style={{ height: '100px' }}></div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F3F4F6',
    paddingBottom: '80px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  heroSection: {
    background: 'linear-gradient(135deg, #0061A6 0%, #003D6B 100%)',
    color: 'white',
    padding: '24px 20px 32px',
    borderBottomLeftRadius: '24px',
    borderBottomRightRadius: '24px',
    boxShadow: '0 4px 20px rgba(0, 97, 166, 0.3)',
  },
  heroContent: {
    maxWidth: '480px',
    margin: '0 auto',
  },
  headerTop: {
    marginBottom: '24px',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  pageTitle: {
    fontSize: '20px',
    fontWeight: '700',
    letterSpacing: '-0.5px',
  },
  balanceCard: {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '20px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    textAlign: 'center',
    marginBottom: '16px',
  },
  balanceLabel: {
    fontSize: '13px',
    opacity: 0.85,
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '500',
  },
  balanceAmount: {
    fontSize: '36px',
    fontWeight: '700',
    letterSpacing: '-1px',
  },
  warningBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    padding: '12px',
    borderRadius: '12px',
    fontSize: '13px',
  },
  warningText: {
    opacity: 0.9,
  },
  contentContainer: {
    padding: '20px',
    maxWidth: '480px',
    margin: '0 auto',
    marginTop: '-10px',
  },
  notifBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    borderRadius: '12px',
    margin: '20px',
    maxWidth: '480px',
    marginLeft: 'auto',
    marginRight: 'auto',
    fontSize: '14px',
    fontWeight: '500',
  },
  card: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '24px',
    marginTop: 0,
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  inputWrapper: {
    position: 'relative',
  },
  input: {
    width: '100%',
    padding: '14px',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    fontSize: '15px',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: 'inherit',
  },
  inputSuffix: {
    position: 'absolute',
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9CA3AF',
    fontWeight: '600',
    fontSize: '14px',
  },
  primaryButton: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #0061A6 0%, #003D6B 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 4px 12px rgba(0, 97, 166, 0.3)',
    transition: 'all 0.2s ease',
  },
  secondaryButton: {
    flex: 1,
    padding: '16px',
    background: 'white',
    color: '#374151',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
  },
  summarySection: {
    background: '#F9FAFB',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '24px',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '12px 0',
    borderBottom: '1px solid #E5E7EB',
  },
  summaryLabel: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: '500',
  },
  summaryValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#111827',
  },
  summaryValueSmall: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    textAlign: 'right',
    maxWidth: '60%',
    wordBreak: 'break-all',
  },
  activationSection: {
    marginBottom: '24px',
  },
  hint: {
    display: 'block',
    marginTop: '6px',
    fontSize: '12px',
    color: '#9CA3AF',
    fontStyle: 'italic',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
  },
  successCard: {
    padding: '32px 24px',
    textAlign: 'center',
    background: '#DCFCE7',
    borderRadius: '20px',
    border: '2px solid #BBF7D0',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#065F46',
    marginTop: '20px',
    marginBottom: '16px',
  },
  successText: {
    fontSize: '15px',
    color: '#047857',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  successDetails: {
    background: 'rgba(255, 255, 255, 0.6)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '24px',
    textAlign: 'left',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
  },
  detailLabel: {
    fontSize: '13px',
    color: '#065F46',
    fontWeight: '600',
  },
  detailValue: {
    fontSize: '13px',
    color: '#047857',
    fontWeight: '600',
    textAlign: 'right',
    maxWidth: '60%',
    wordBreak: 'break-word',
  },
  successButton: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)',
    transition: 'all 0.2s ease',
  },
  errorCard: {
    padding: '32px 24px',
    textAlign: 'center',
    background: '#FEE2E2',
    borderRadius: '20px',
    border: '2px solid #FECACA',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#991B1B',
    marginTop: '20px',
    marginBottom: '16px',
  },
  errorMessageCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '16px',
    padding: '20px',
    margin: '20px 0 24px',
    border: '2px solid rgba(220, 38, 38, 0.2)',
  },
  errorMessageText: {
    fontSize: '15px',
    color: '#7F1D1D',
    lineHeight: '1.6',
    margin: 0,
    fontWeight: '500',
  },
  retryButton: {
    width: '100%',
    padding: '16px',
    background: 'white',
    color: '#374151',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
  },
};

export default Virement;